import React, { useEffect, useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBook, faTimes } from '@fortawesome/free-solid-svg-icons';
import { getIndicatorDoc, listIndicatorDocs } from '../data/indicatorDocs';

function Section({ title, children }) {
  if (!children) return null;
  return (
    <section className="space-y-2">
      <h3 className="text-xs font-bold uppercase tracking-wider text-indigo-700">{title}</h3>
      {children}
    </section>
  );
}

function BulletList({ items }) {
  if (!items?.length) return null;
  return (
    <ul className="space-y-1.5 text-[13px] text-gray-700 leading-relaxed list-disc pl-4">
      {items.map((item, i) => (
        <li key={i}>{item}</li>
      ))}
    </ul>
  );
}

/**
 * Modal with write-ups for every research indicator.
 * @param {{ open: boolean, onClose: () => void, initialId?: string }} props
 */
export default function IndicatorDocsModal({ open, onClose, initialId }) {
  const all = useMemo(() => listIndicatorDocs(), []);
  const [activeId, setActiveId] = useState(initialId || all[0]?.id || 'markov');

  useEffect(() => {
    if (open && initialId) setActiveId(initialId);
  }, [open, initialId]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  if (!open) return null;

  const doc = getIndicatorDoc(activeId) || all[0];

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/55 px-3 sm:px-4"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Indicator documentation"
    >
      <div
        className="w-full max-w-4xl max-h-[88vh] rounded-xl border border-gray-200 bg-white shadow-2xl overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between bg-gray-50 shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            <FontAwesomeIcon icon={faBook} className="text-indigo-600" />
            <h2 className="text-base font-bold text-gray-900 truncate">Indicator docs</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="h-8 w-8 rounded-lg text-gray-500 hover:bg-gray-200 hover:text-gray-800 inline-flex items-center justify-center"
            aria-label="Close"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="flex flex-col sm:flex-row min-h-0 flex-1">
          <nav className="sm:w-52 shrink-0 border-b sm:border-b-0 sm:border-r border-gray-200 bg-gray-50/80 overflow-x-auto sm:overflow-y-auto max-h-36 sm:max-h-none">
            <ul className="flex sm:flex-col gap-0.5 p-2">
              {all.map((item) => {
                const active = item.id === doc?.id;
                return (
                  <li key={item.id} className="shrink-0 sm:shrink">
                    <button
                      type="button"
                      onClick={() => setActiveId(item.id)}
                      className={`w-full text-left rounded-lg px-2.5 py-2 text-xs transition-colors whitespace-nowrap sm:whitespace-normal ${
                        active
                          ? 'bg-indigo-100 text-indigo-900 font-semibold'
                          : 'text-gray-600 hover:bg-white hover:text-gray-900'
                      }`}
                    >
                      <div>{item.title}</div>
                      <div className="text-[9px] uppercase tracking-wide text-gray-400 mt-0.5 hidden sm:block">
                        {item.category}
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          </nav>

          <div className="flex-1 overflow-y-auto p-4 sm:p-5 space-y-5 min-h-0">
            {doc ? (
              <>
                <header>
                  <div className="text-[10px] font-semibold uppercase tracking-wider text-indigo-600">
                    {doc.category}
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mt-0.5">{doc.title}</h3>
                  <p className="mt-2 text-sm text-gray-600 leading-relaxed">{doc.summary}</p>
                </header>

                <Section title="What it is">
                  <BulletList items={doc.whatItIs} />
                </Section>

                <Section title="How it works">
                  <BulletList items={doc.howItWorks} />
                </Section>

                <Section title="How to read the output">
                  <BulletList items={doc.howToRead} />
                </Section>

                <Section title="Parameters">
                  <div className="rounded-lg border border-gray-200 overflow-hidden">
                    <table className="w-full text-[12px]">
                      <thead className="bg-gray-50 text-gray-500">
                        <tr className="text-left">
                          <th className="px-3 py-2 font-semibold w-32">Param</th>
                          <th className="px-3 py-2 font-semibold">Meaning</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(doc.parameters || []).map((p) => (
                          <tr key={p.name} className="border-t border-gray-100">
                            <td className="px-3 py-2 font-semibold text-gray-800 align-top">{p.name}</td>
                            <td className="px-3 py-2 text-gray-600 leading-relaxed">{p.meaning}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </Section>

                <Section title="How to use it">
                  <BulletList items={doc.howToUse} />
                </Section>

                <Section title="Risks & caveats">
                  <BulletList items={doc.risks} />
                </Section>

                {doc.credit ? (
                  <p className="text-[11px] text-gray-400 border-t border-gray-100 pt-3 leading-relaxed">
                    {doc.credit}
                  </p>
                ) : null}
              </>
            ) : (
              <p className="text-sm text-gray-500">No documentation for this indicator yet.</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
